import { apiEndpoints, CommandResult, EmployeeToken, Partner, TokenReadResult } from '../amorg-common'
import { refresh } from "./authClientApi";
import { errorCodes } from '../amorg-common'
import { PermissionGroup, PermissionGroups } from '../amorg-common'
import { Permission } from '../amorg-common'
import { UserToken } from '../amorg-common'
import { clientTokenReader } from '../utils/clientTokenReader';
import { applicationEndpoints } from '../amorg-common/applicationEndpoints';

export const getLoginUrl = () => `/login?return=${document.location.href}`; 

const LOG_MAYBE = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logMaybe = (message?: any, ...optionalParams: any[]) => {
    if(!LOG_MAYBE) 
        return;

    console.log(message, optionalParams);
}

export const redirectToEmployeeLogin = () =>
{
    // Skip if already on login page, to avoid infinite reload loop
    if(document.location.href.search("login\\?return\\=") >= 0)
        return;

    try {
        const user = getCookieEmployee();
        // If user is not an employee, redirect to user login
        if(user.status === 'expired' && user.token.type === 'user')
            redirectToLogin();

        if(user.status === 'expired' && user.token.type === 'employee')
        {
            document.location.assign(applicationEndpoints.portal_login_siteId.replace('{siteId}', user.token.siteId) + `?return=${document.location.href}`);
        }

        document.location.assign(applicationEndpoints.portal_login + `?return=${document.location.href}`);
    }
    finally{
        document.location.assign(applicationEndpoints.portal_login + `?return=${document.location.href}`);
    }
}

export const redirectToLogin = () => 
{
    let siteId = '';

    // Skip if already on login page, to avoid infinite reload loop
    if(document.location.href.search("login\\?return\\=") >= 0)
        return;

    // Try to redirect to login with siteId embedded
    try {
        const user = getCookieUser();
        if(user?.status === 'expired' && user?.token?.siteId)
            siteId = user.token.siteId;
    }
    finally{
        let url = '/login';
        if(siteId)
            url += `/${siteId}`;

        document.location.assign(`${url}?return=${document.location.href}`);
    }
}

export const redirectToError = () => {
    // document.location.assign('/error');
}

export const redirectToErrorMessage = (message:string) => {
    document.location.assign(`/error?message=${message}`);
}

export const redirectToInsufficientPermission = () => {
    document.location.assign('/insufficientpermission');
}

export const authWrapper = async(runner:()=>Promise<Response>, skipLogin?:boolean):Promise<Response> =>
{
    const response = await runner();
    if(response.status === 401){

        const refreshResult = await refresh();
        if(refreshResult.isSuccess){
            return await runner();
        }
        else {
            if(skipLogin){
                return response;
            }else{
                redirectToLogin();
            }  
        }
    }

    return response;
}

async function handleSiteDisabled(status:number, text:string)
{
    if(status === 400 && text === 'site_disabled'){
        redirectToErrorMessage('<p>Hov.<br/><br/>Det pågældende site er ikke tilgængeligt.<br/><br/>Vi anbefaler at du kontakter din lokale AMO repræsentant.<br/><br/>Mvh. AMORG</p>');
    }
}

const baseCommandAuthWrapper = async( runner:()=>Promise<Response>):Promise<CommandResult> =>
{
    const response = await authWrapper(runner);
    if(response.status === 200)
        return CommandResult.succeeds();
    else{
        const text = await response.text() as errorCodes;
        logMaybe(`command execution failed: ${response.status} / ${text}`);
        await handleSiteDisabled(response.status, text);
        return CommandResult.fails(text);
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const commandAuthWrapper = async(url:string, method:'POST' | 'PUT' | 'DELETE', requestBody?:any):Promise<CommandResult>=>
{
    logMaybe(`commandAuthWrapper|calling ${method} ${url} body:${JSON.stringify(requestBody || {})}`);
    return baseCommandAuthWrapper(
        () => fetch(`${url}`, 
            { 
                method,
                cache:'no-cache',
                headers: { 'Content-Type': 'application/json' },
                body: requestBody ? JSON.stringify(requestBody) : undefined
            }
        )
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const rawCommandAuthWrapper = async(url:string, method:'POST' | 'PUT' | 'DELETE', rawBody?:any, 
    headers?:HeadersInit, abortSignal?:AbortSignal):Promise<CommandResult>=>
{
    logMaybe(`commandAuthWrapper|calling ${method} ${url} body:${rawBody ? 'value' : 'undefined'}`);
    return baseCommandAuthWrapper(
        () => fetch(`${url}`, 
            { 
                method,
                cache:'no-cache',
                headers:headers || { 'Content-Type': 'application/json' },
                body: rawBody || undefined,
                signal:abortSignal
            }
        )
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const baseQueryAuthWrapper = async( runner:()=>Promise<Response>, skipLogin?:boolean):Promise<any> =>
{
    const response = await authWrapper(runner, skipLogin);
    if(response.status === 200)
        return response.json();
    else{
        const text = await response.text();
        logMaybe(`query execution failed: ${response.status} / ${text}`);
        await handleSiteDisabled(response.status, text);
        redirectToError();
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const queryAuthWrapper = async(url:string, skipLogin?:boolean):Promise<any> => {
    return baseQueryAuthWrapper(
        () => fetch(`${url}`,
            { 
                method:'GET',
                cache:'no-cache',
                headers:{ 'Content-Type': 'application/json' }
            }
        ),
        skipLogin
    );
}

// Use this for queries with a lot of parameters
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const postQueryAuthWrapper = async(url:string, requestBody?:any, skipLogin?:boolean):Promise<any> => {
    return baseQueryAuthWrapper(
        () => fetch(`${url}`,
            { 
                method:'POST',
                cache:'no-cache',
                headers:{ 'Content-Type': 'application/json' },
                body: requestBody ? JSON.stringify(requestBody) : undefined
            }
        ),
        skipLogin
    );
}

export function getCookieUser(): TokenReadResult<UserToken>{
    const allCookies = document.cookie;
    if(!allCookies)
        return { status: 'no-token' };
    
    const jwtEntry = allCookies
        .split('; ')
        .find(kvp => kvp.startsWith(`a=`));
        
    if(!jwtEntry)
        return { status: 'no-token' };

    const jwtCookie = jwtEntry.split('=')[1];
    
    return clientTokenReader.tryReadUserToken(jwtCookie);
}

export function getCookieEmployee(): TokenReadResult<EmployeeToken | UserToken>{
    const allCookies = document.cookie;
    if(!allCookies)
        return { status: 'no-token' };
    
    const jwtEntry = allCookies
        .split('; ')
        .find(kvp => kvp.startsWith(`a=`));
        
    if(!jwtEntry)
        return { status: 'no-token' };

    const jwtCookie = jwtEntry.split('=')[1];
    
    return clientTokenReader.tryReadAccessToken(jwtCookie);
}

export function hasUserPermissions(tokenUser:UserToken | null, permissions:Permission[])
{
    const permissionGroup = tokenUser === null
        ? PermissionGroups.anonymous
        : PermissionGroups.all.find(g => g.id === tokenUser.permissionGroupId);
    if(!permissionGroup)
        throw new Error('Unable to determine permissiongroup');
    
    return hasPermissions(permissionGroup, permissions);
}

export function hasPermissions(permissionGroup:PermissionGroup, permissions:Permission[])
{
    for(const requiredPermission of permissions){
        if(!permissionGroup.permissions.find(p => p === requiredPermission))
            return false;
    }
    return true;
}

export async function getAuthorizedActiveUserOrRedirect(requiredPermissions:Permission[]):Promise<UserToken>
{
    const userResult = await getMaybeActiveUserOrRefresh(requiredPermissions);
    if(!userResult.hasAccess)
        redirectToInsufficientPermission();

    if(userResult.tokenUser === null)
        throw new Error('Unexpected token type - user token expected');

    return userResult.tokenUser;
}

export async function getMaybeActiveUserOrRefresh(requiredPermissions?:Permission[])
    :Promise<{hasAccess:boolean, tokenUser:UserToken|null}>
{
    return getMaybeActiveUserOrRefreshInternal(requiredPermissions || [], 3);
}

async function getMaybeActiveUserOrRefreshInternal(requiredPermissions: Permission[], refreshAttemptsLeft: number)
    :Promise<{hasAccess:boolean, tokenUser:UserToken|null}>
{
    const cookieUser = getCookieUser();

    if(cookieUser.status === 'no-token'){
        // Replacing this with below, when splitting app and website, and changing root url from pointing to website, to pointing to overview.
        // This way, a missing login token is treated as if the token was expired.
        // -------------
        // return hasPermissions(permissionGroup, requiredPermissions)
        //         ? { hasAccess:true, tokenUser:null }
        //         : { hasAccess:false, tokenUser:null};

        if(!hasPermissions(PermissionGroups.anonymous, requiredPermissions))
        {
            redirectToLogin();
            return { hasAccess:false, tokenUser:null}; // This line will likely never be hit, as the redirect should happen before, but is needed to satisfy return type.
        }
        else
            return { hasAccess:true, tokenUser:null };
    } 
    
    // Will catch and re-run entire function, eliminating the need to handle it
    else if(cookieUser.status === 'expired'){
        if(refreshAttemptsLeft <= 0)
        {
            redirectToLogin();
            return { hasAccess:false, tokenUser:null };
        }

        const result = await refresh();
        if(result.isSuccess)
            return await getMaybeActiveUserOrRefreshInternal(requiredPermissions, refreshAttemptsLeft - 1);
        else
            redirectToLogin();
    }

    else if(cookieUser.status === 'invalid'){
        redirectToLogin();
    }

    else if(cookieUser.status === 'valid'){
        const permissionGroup = PermissionGroups.all.find(pg => pg.id === cookieUser.token.permissionGroupId);
        if(!permissionGroup)            
            throw new Error('Unable to determine permissiongroup');

        return hasPermissions(permissionGroup, requiredPermissions)
            ? { hasAccess:true, tokenUser:cookieUser.token }
            : { hasAccess:false, tokenUser:null};
    }

    else
    {
        throw new Error(`Unhandled condition - cookie status`);
    }

    return { hasAccess:false, tokenUser:null}; // This line will likely never be hit, as all conditions should be handled above, but is needed to satisfy return type.
}

export async function getMaybeActiveEmployeeOrLogin(): Promise<EmployeeToken | UserToken>
{
    const result = getCookieEmployee();
    if(result.status === 'valid')
    {
        logMaybe('Active employee token found in cookie');
        return result.token;
    }
    else if(result.status === 'expired' && result.token.type === 'user')
    {
        logMaybe('User token found in cookie, but expired - redirecting to regular user login');
        const result = await getMaybeActiveUserOrRefresh();
        if(result.hasAccess)
        {
            logMaybe('User token refresh successful, user has access - returning user token');
            if(result.tokenUser === null)
                throw new Error('Unexpected token type - employee token expected');

            return result.tokenUser;
        }
        logMaybe('User token refresh did not result in access - redirecting to login');
        redirectToLogin();
    }
    else
    {
        logMaybe('No valid employee token found in cookie - redirecting to employee login');
        redirectToEmployeeLogin();
    }

    throw new Error('Unreachable code reached in getMaybeActiveEmployeeOrLogin');
}

export async function getPartnerBySiteId(siteId:string):Promise<Partner | null>
{
    return queryAuthWrapper(apiEndpoints.site_siteId_partner.replace(':siteId', siteId), true) as Promise<Partner | null>;
}
